import React, { useEffect, useState } from "react";
import { RefreshCw, Search, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { VoiceAnalytics } from "@/components/VoiceAnalytics";

function Stat({ label, value, testid }) {
  return <div className="bg-card rounded-2xl border border-border p-4" data-testid={testid}><p className="text-xs text-muted-foreground">{label}</p><p className="font-display font-extrabold text-2xl text-ink">{value}</p></div>;
}

function Table({ title, rows, cols, testid }) {
  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden" data-testid={testid}>
      <h3 className="font-semibold text-sm px-4 py-3 border-b border-border">{title}</h3>
      {rows.length === 0 ? <p className="text-xs text-muted-foreground p-4">Nothing yet.</p> : (
        <table className="w-full text-sm"><tbody>
          {rows.map((r, i) => <tr key={i} className="border-t border-border first:border-0">{cols.map((c, j) => <td key={j} className={`px-4 py-2 ${j ? "text-right text-muted-foreground" : "font-medium"}`}>{c(r)}</td>)}</tr>)}
        </tbody></table>
      )}
    </div>
  );
}

export function AdminSearchPanel() {
  const [a, setA] = useState(null);
  const [q, setQ] = useState("");
  const [ex, setEx] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/search/analytics").then(({ data }) => setA(data)).catch(() => toast.error("Could not load search analytics"));
  useEffect(() => { load(); }, []);
  const reindex = async () => { setBusy(true); try { const { data } = await api.post("/admin/search/reindex"); toast.success(`Reindexed ${data.products + data.other} entries in ${data.seconds}s`); load(); } catch { toast.error("Reindex failed"); } finally { setBusy(false); } };
  const test = async (e) => { e?.preventDefault(); if (!q.trim()) return; setBusy(true); try { const { data } = await api.get("/admin/search/explain", { params: { q } }); setEx(data); } catch { toast.error("Test failed"); } finally { setBusy(false); } };
  if (!a) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald" /></div>;
  return (
    <div className="space-y-6" data-testid="admin-search-panel">
      <VoiceAnalytics />
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-muted-foreground">Index: <b className="text-ink">{a.index.total.toLocaleString()}</b> entries · {Object.entries(a.index.by_kind).map(([k, n]) => `${k} ${n}`).join(" · ")}<br />Last full reindex: {a.index.last_full_reindex ? new Date(a.index.last_full_reindex).toLocaleString() : "pending"} — auto-syncs after every catalogue change and every 10 min.</div>
        <button onClick={reindex} disabled={busy} data-testid="search-reindex-btn" className="ml-auto inline-flex items-center gap-2 rounded-full bg-ink text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"><RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Reindex search database</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label={`Searches (${a.days}d)`} value={a.total_searches.toLocaleString()} testid="search-stat-total" />
        <Stat label="Zero-result searches" value={`${a.zero_results} (${a.zero_rate}%)`} testid="search-stat-zero" />
        <Stat label="Result clicks" value={`${a.clicks} (${a.click_rate}%)`} testid="search-stat-clicks" />
        <Stat label="Search → purchase" value={a.search_to_purchase} testid="search-stat-purchase" />
        <Stat label="Trending (48h)" value={a.trending[0]?._id || "—"} testid="search-stat-trending" />
      </div>

      <form onSubmit={test} className="bg-card rounded-2xl border border-border p-4" data-testid="search-diagnostics">
        <h3 className="font-semibold text-sm mb-2 flex items-center gap-2"><Search size={14} /> Test search / diagnostics</h3>
        <div className="flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Type any query, e.g. waterr pump" data-testid="search-test-input" className="flex-1 rounded-full border border-border px-4 py-2 text-sm" />
          <button disabled={busy} data-testid="search-test-btn" className="rounded-full bg-emerald text-white px-5 py-2 text-sm font-semibold">Test</button>
        </div>
        {ex && (
          <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm" data-testid="search-explain">
            <div className="space-y-1">
              <p><b>Normalized:</b> {ex.normalized}</p>
              <p><b>Tokens → stems:</b> {ex.tokens.join(", ")} → {ex.stems.join(", ")}</p>
              <p><b>Typo correction:</b> {ex.typo_correction ? ex.typo_correction.join(" ") : "none needed"}</p>
              <p><b>Detected intent:</b> {ex.detected_intent}</p>
              <p><b>Matched keywords:</b> {ex.matched_terms.join(", ") || "—"}</p>
              <p><b>Synonyms used:</b> {ex.synonyms_used.join(", ") || "—"}</p>
              <p><b>Index queried:</b> {ex.index_queried}</p>
              <p><b>Filters:</b> {JSON.stringify(ex.filters)}</p>
              <p><b>Results:</b> {ex.total}{ex.corrected_from ? ` (corrected from “${ex.corrected_from}”)` : ""}</p>
            </div>
            <div className="space-y-2 max-h-80 overflow-auto">
              {ex.sections.map((s) => (
                <div key={s.kind}><p className="text-xs font-bold uppercase text-muted-foreground">{s.label} ({s.total})</p>
                  {s.items.slice(0, 5).map((it) => <p key={it.id} className="text-xs border-l-2 border-emerald pl-2 my-1"><b>{it.score}</b> · {it.title} <span className="text-muted-foreground">— {it.why?.join("; ")}</span></p>)}
                </div>
              ))}
              {ex.total === 0 && <p className="text-xs text-coral">No results. Did you mean: {ex.suggestions?.did_you_mean?.join(", ") || "—"}</p>}
            </div>
          </div>
        )}
      </form>

      <div className="grid lg:grid-cols-2 gap-4">
        <Table title="⚠ Zero-result report — what customers searched but could not find" testid="search-zero-report" rows={a.zero_result_report} cols={[(r) => <span className="flex items-center gap-2"><AlertTriangle size={13} className="text-coral" />{r.raw}</span>, (r) => `${r.n}×`, (r) => new Date(r.last).toLocaleDateString()]} />
        <Table title="Most searched terms" testid="search-top-terms" rows={a.top_terms} cols={[(r) => r.raw, (r) => `${r.n}×`, (r) => (r.zero ? `${r.zero} zero` : "")]} />
        <Table title="Misspelled searches (auto-corrected)" testid="search-misspelled" rows={a.misspelled} cols={[(r) => r._id, (r) => `→ ${r.to}`, (r) => `${r.n}×`]} />
        <Table title="Most clicked results" testid="search-top-clicked" rows={a.top_clicked} cols={[(r) => r._id, (r) => r.kind, (r) => `${r.n}×`]} />
        <Table title="Popular categories (from search clicks)" testid="search-popular-cats" rows={a.popular_categories} cols={[(r) => r._id, (r) => `${r.n}×`]} />
        <Table title="Search volume by day" testid="search-volume" rows={a.volume_by_day.slice(-14)} cols={[(r) => r._id, (r) => `${r.n} searches`, (r) => `${r.zero} zero`]} />
      </div>
    </div>
  );
}
